import { native } from '@forest/native-services'
import {
  classifyPurchaseError,
  getChinaAndroidTrialProductId,
  tagFailure,
  waitForSubscriptionStatus,
  withFailure,
} from '@forest/paywall-core'
import { useMutation } from '@tanstack/solid-query'
import { showDialog } from '~/components/dialog'
import {
  AbortError,
  getAuthenticatedUserInfo,
} from '~/lib/get-authenticated-user-info'
import { m } from '~/paraglide/messages'
import { api } from '~/services/api'
import { $subscribedView } from '~/store'
import { showSubscriptionConflictDialog } from '~/lib/subscription-conflict-dialog'
import { showPurchaseConfirmationDialog } from '~/lib/purchase-confirmation-dialog'

const CHINA_ANDROID_TRIAL_PRODUCT_ID = getChinaAndroidTrialProductId(
  import.meta.env.MODE,
)

export function useChinaAndroidTrialMutation(params?: {
  onSuccess?: () => void
}) {
  const { onSuccess } = params ?? {}

  return useMutation(() => ({
    mutationFn: async () => {
      const userInfo = await getAuthenticatedUserInfo().catch(
        (error: unknown) => {
          throw tagFailure(error, 'auth')
        },
      )
      await api.service
        .post(`store/users/${userInfo.user_id}/purchase-right`, {
          json: {
            product_id: CHINA_ANDROID_TRIAL_PRODUCT_ID,
            iap_sku: 'cna_plus_trial_7',
          },
        })
        .catch((error: unknown) => {
          throw tagFailure(error, 'create_order')
        })
      await waitForSubscriptionStatus({
        serviceApi: api.service,
        userId: userInfo.user_id,
      }).catch(() => {
        // Assume subscribed if polling fails; handled by subsequent refresh
      })
    },
    onSuccess: () => {
      native.general.logEvent({
        event_name: 'plus_trial_success',
        event_params: {
          sku_id: 'cna_plus_trial_7',
        },
      })
      $subscribedView.set({ isChinaAndroidTrial: true })
      native.user.refreshServiceLevel()
      onSuccess?.()
    },
    onError: (error) => {
      if (error instanceof AbortError) return

      const intent = classifyPurchaseError(error)
      switch (intent.kind) {
        case 'no_internet':
          showDialog({
            title: m.dialog_no_internet_title(),
            description: withFailure(
              m.dialog_no_internet_content(),
              intent.failure,
            ),
            buttonText: m.OK(),
          })
          return
        case 'confirmation_pending':
          showPurchaseConfirmationDialog(intent.failure)
          return
        case 'subscription_conflict':
          void showSubscriptionConflictDialog(intent.ownerUid, intent.failure)
          return
        case 'payment_timeout':
        case 'illegal_transaction_restore':
        case 'http_error':
        case 'unknown':
          showDialog({
            title: m.dialog_unknown_error_title(),
            description: withFailure(
              m.dialog_unknown_error_content({
                error_code: intent.failure.code,
              }),
              intent.failure,
              { codeInDescription: true },
            ),
            buttonText: m.OK(),
            buttonAction: undefined,
          })
          return
        default:
          intent satisfies never
      }
    },
  }))
}
